import {
  catalogApi,
  fetchPage,
  type CatalogRow,
  type ListParams,
  type PageResponse,
} from './collections';
import { apiFetch } from './http';

/**
 * The notification centre (13 §6.2): the recipient's own inbox and mute switches under nothing
 * but a tenant session, and the templates, rules and delivery log of `admin.notifications.manage`
 * (13 §4.12, 05g §7). Templates and rules are bounded catalogs answered whole (07 §1.7, AM-129),
 * the same shape as `/asset-types`; the delivery log is a cursor-paginated diagnostic, read-only.
 */

/** The two channels the MVP actually dispatches (05g §7.1); the other three exist in the schema for Fase 2. */
export const NOTIFICATION_CHANNELS = ['email', 'in_app'] as const;

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** One row of the recipient's own inbox (07 §4.2). */
export interface Notification extends CatalogRow {
  recipientUserId: string;
  notificationType: string;
  notificationRuleId: string | null;
  entityType: string | null;
  entityId: string | null;
  title: string;
  body: string;
  payload: unknown;
  readAt: string | null;
  correlationId: string | null;
  createdAt: string;
}

/** One row of the recipient's own mute preferences (05g §7.2); no `id` or `eTag` of its own. */
export interface NotificationPreference {
  notificationType: string;
  channel: string;
  isMuted: boolean;
}

/** What `GET /me/notification-preferences/catalog` offers, so the mute screen hardcodes neither list. */
export interface NotificationPreferenceCatalog {
  types: string[];
  channels: string[];
}

/** A row of `notification_templates` (07 §4.14, AM-129); `key`, `channel` and `locale` are immutable after creation. */
export interface NotificationTemplate extends CatalogRow {
  key: string;
  channel: string;
  locale: string;
  subject: string | null;
  bodyTemplate: string;
  variables: string[];
  isActive: boolean;
  createdAt: string;
}

/** A row of `notification_rules` (07 §4.14, AM-129); `eventType` is immutable after creation. */
export interface NotificationRule extends CatalogRow {
  name: string;
  eventType: string;
  /** `{}` always true, or the AST of `RuleCondition` (05g §7.1); opaque to the console. */
  condition: unknown;
  /** `{"targets": [...]}` (`Rules.RecipientSelector`); opaque to the console. */
  recipientSelector: unknown;
  channels: string[];
  templateId: string | null;
  isActive: boolean;
  priorityOrder: number;
  createdAt: string;
}

/** The four values of `ck_notification_deliveries_status` (`NotificationDeliveryStatuses`). */
export const NOTIFICATION_DELIVERY_STATUSES = ['pending', 'sent', 'failed', 'suppressed'] as const;

export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

/** The five values of `ck_notification_deliveries_channel`; only the first two are dispatched today (05g §7.1). */
export const NOTIFICATION_DELIVERY_CHANNELS = [
  'email',
  'in_app',
  'teams',
  'webhook',
  'push',
] as const;

/**
 * One diagnostic delivery attempt (05g §7.2); read-only, `admin.notifications.manage`. It names the
 * type of its notification and who it was for, and never the title or the body.
 */
export interface NotificationDelivery extends CatalogRow {
  notificationId: string;
  notificationType: string;
  recipientUserId: string;
  recipientName: string | null;
  channel: string;
  status: string;
  recipientAddress: string | null;
  attempts: number;
  lastError: string | null;
  providerMessageId: string | null;
  sentAt: string | null;
  createdAt: string;
}

/** One event of the outbox with the variables its payload carries (AM-129 point 5). */
export interface NotificationEvent {
  eventType: string;
  variables: string[];
}

export const notificationsApi = {
  list: (params: ListParams, signal?: AbortSignal): Promise<PageResponse<Notification>> =>
    fetchPage<Notification>('/api/v1/me/notifications', params, signal),
  markRead: (id: string): Promise<Notification> =>
    apiFetch<Notification>(`/api/v1/me/notifications/${id}/read`, { method: 'POST' }),
  markAllRead: (): Promise<{ marked: number }> =>
    apiFetch<{ marked: number }>('/api/v1/me/notifications/read-all', { method: 'POST' }),
  preferences: (signal?: AbortSignal): Promise<{ items: NotificationPreference[] }> =>
    apiFetch<{ items: NotificationPreference[] }>(
      '/api/v1/me/notification-preferences',
      signal === undefined ? {} : { signal },
    ),
  /** Replaces the recipient's whole muted set (05g §7.2): only the entries the caller marks muted matter. */
  replacePreferences: (
    items: NotificationPreference[],
  ): Promise<{ items: NotificationPreference[] }> =>
    apiFetch<{ items: NotificationPreference[] }>('/api/v1/me/notification-preferences', {
      method: 'PUT',
      body: { items },
    }),
  preferenceCatalog: (signal?: AbortSignal): Promise<NotificationPreferenceCatalog> =>
    apiFetch<NotificationPreferenceCatalog>(
      '/api/v1/me/notification-preferences/catalog',
      signal === undefined ? {} : { signal },
    ),
};

export const notificationTemplatesApi = catalogApi<NotificationTemplate>(
  '/api/v1/notification-templates',
);

export const notificationRulesApi = catalogApi<NotificationRule>('/api/v1/notification-rules');

/** `GET /notification-events` (AM-129 point 5): read-only, no create/update surface of its own. */
export const notificationEventsApi = {
  list: (signal?: AbortSignal): Promise<{ items: NotificationEvent[] }> =>
    apiFetch<{ items: NotificationEvent[] }>(
      '/api/v1/notification-events',
      signal === undefined ? {} : { signal },
    ),
};

/**
 * `GET /notification-deliveries` (05g §7.2): cursor-paginated. The one write is `retry`: a `failed`
 * email goes back to the queue with its attempts at zero, guarded by the ETag the row was read with
 * (`409 delivery_not_retryable` when it is not a failed email any more, `409 recipient_without_email`
 * while the recipient still has no address, `412` when it changed since).
 */
export const notificationDeliveriesApi = {
  list: (params: ListParams, signal?: AbortSignal): Promise<PageResponse<NotificationDelivery>> =>
    fetchPage<NotificationDelivery>('/api/v1/notification-deliveries', params, signal),
  retry: (id: string, etag: string): Promise<NotificationDelivery> =>
    apiFetch<NotificationDelivery>(`/api/v1/notification-deliveries/${id}/retry`, {
      method: 'POST',
      headers: { 'If-Match': etag },
    }),
};
